<template>
  <div class="unified-query-new">
    <!-- 智能仪表板 -->
    <div class="dashboard-section">
      <div class="dashboard-header">
        <h2 class="section-title">
          <i class="h-icon-analytics"></i>
          AI财务数据指挥中心
        </h2>
        <Button @click="showAllData" :type="isShowingAll() ? 'primary' : 'default'">
          <i class="h-icon-apps"></i>
          显示全部
        </Button>
      </div>
      <div class="dashboard-cards">
        <div class="stat-card" @click="filterByType('documents')" :class="{ 'active': isCardActive('documents') }">
          <div class="card-icon document-icon">🎫</div>
          <div class="card-content">
            <h3>票据管理</h3>
            <div class="stat-number">{{ statistics.documentStats.totalCount || 0 }}</div>
            <div class="stat-details">
              <span>已归并: {{ statistics.documentStats.mergedCount || 0 }}</span>
              <span>已关联: {{ statistics.documentStats.relatedCount || 0 }}</span>
            </div>
          </div>
          <div class="card-filter-hint">点击筛选票据</div>
        </div>

        <div class="stat-card" @click="filterByType('receipts')" :class="{ 'active': isCardActive('receipts') }">
          <div class="card-icon receipt-icon">💳</div>
          <div class="card-content">
            <h3>银行回单</h3>
            <div class="stat-number">{{ statistics.receiptStats.totalCount || 0 }}</div>
            <div class="stat-details">
              <span>已归并: {{ statistics.receiptStats.mergedCount || 0 }}</span>
              <span>已关联: {{ statistics.receiptStats.relatedCount || 0 }}</span>
            </div>
          </div>
          <div class="card-filter-hint">点击筛选银证</div>
        </div>

        <div class="stat-card" @click="filterByType('merge-groups')" :class="{ 'active': isCardActive('merge-groups') }">
          <div class="card-icon group-icon">📦</div>
          <div class="card-content">
            <h3>归并组</h3>
            <div class="stat-number">{{ (statistics.mergeGroupStats.documentGroupCount || 0) + (statistics.mergeGroupStats.receiptGroupCount || 0) }}</div>
            <div class="stat-details">
              <span>票据组: {{ statistics.mergeGroupStats.documentGroupCount || 0 }}</span>
              <span>银证组: {{ statistics.mergeGroupStats.receiptGroupCount || 0 }}</span>
            </div>
          </div>
          <div class="card-filter-hint">点击筛选归并组</div>
        </div>

        <div class="stat-card" @click="filterByStatus('related')" :class="{ 'active': isStatusActive('related') }">
          <div class="card-icon relation-icon">🔗</div>
          <div class="card-content">
            <h3>关联关系</h3>
            <div class="stat-number">{{ statistics.relationStats.totalRelationCount || 0 }}</div>
            <div class="stat-details">
              <span>AI生成: {{ statistics.relationStats.aiGeneratedRelationCount || 0 }}</span>
              <span>手动创建: {{ statistics.relationStats.manualRelationCount || 0 }}</span>
            </div>
          </div>
          <div class="card-filter-hint">点击筛选已关联</div>
        </div>

        <div class="stat-card" @click="filterByType('vouchers')" :class="{ 'active': isCardActive('vouchers') }">
          <div class="card-icon voucher-icon">📄</div>
          <div class="card-content">
            <h3>会计凭证</h3>
            <div class="stat-number">{{ statistics.voucherStats.totalVoucherCount || 0 }}</div>
            <div class="stat-details">
              <span>AI生成: {{ statistics.voucherStats.aiGeneratedVoucherCount || 0 }}</span>
              <span>待生成: {{ (statistics.voucherStats.pendingVoucherDocumentCount || 0) + (statistics.voucherStats.pendingVoucherReceiptCount || 0) }}</span>
            </div>
          </div>
          <div class="card-filter-hint">点击筛选凭证</div>
        </div>

        <div class="stat-card ai-status-card" @click="filterByStatus('ai-processed')" :class="{ 'active': isStatusActive('ai-processed') }">
          <div class="ai-status-indicator" :class="{ 'online': statistics.aiProcessStats.aiServiceAvailable }"></div>
          <div class="card-icon ai-icon">🤖</div>
          <div class="card-content">
            <h3>AI处理状态</h3>
            <div class="stat-number">{{ statistics.aiProcessStats.totalAiTaskCount || 0 }}</div>
            <div class="stat-details">
              <span>已完成: {{ statistics.aiProcessStats.completedAiTaskCount || 0 }}</span>
              <span>运行中: {{ statistics.aiProcessStats.runningAiTaskCount || 0 }}</span>
            </div>
          </div>
          <div class="card-filter-hint">点击筛选AI处理</div>
        </div>
      </div>
    </div>

    <!-- 智能搜索筛选区域 -->
    <div class="search-section">
      <div class="search-header">
        <h3>
          <i class="h-icon-search"></i>
          智能搜索筛选
        </h3>
        <Button @click="toggleSearchPanel">
          <i :class="searchPanelCollapsed ? 'h-icon-down' : 'h-icon-up'"></i>
          {{ searchPanelCollapsed ? '展开' : '收起' }}
        </Button>
      </div>

      <div class="search-content" v-show="!searchPanelCollapsed">
        <div class="search-form-container">
          <!-- 第一行：搜索框和操作按钮 -->
          <div class="search-row">
            <div class="search-input-group">
              <Input v-model="searchForm.keyword"
                     placeholder="搜索编号、摘要、交易方..."
                     @on-enter="executeSearch"
                     clearable>
                <i class="h-icon-search" slot="prefix"></i>
              </Input>
            </div>
            <div class="search-actions">
              <Button type="primary" @click="executeSearch" :loading="searchLoading">
                <i class="h-icon-search"></i>
                搜索
              </Button>
              <Button @click="resetAndSearch">
                <i class="h-icon-refresh"></i>
                重置
              </Button>
              <Button @click="saveSearchScheme">
                <i class="h-icon-bookmark"></i>
                保存方案
              </Button>
            </div>
          </div>

          <!-- 第二行：数据类型筛选 -->
          <div class="filter-row">
            <div class="filter-group">
              <label>数据类型</label>
              <CheckboxGroup v-model="searchForm.entityTypes">
                <Checkbox value="DOCUMENT">票据</Checkbox>
                <Checkbox value="RECEIPT">银证</Checkbox>
                <Checkbox value="DOCUMENT_GROUP">票据组</Checkbox>
                <Checkbox value="RECEIPT_GROUP">银证组</Checkbox>
                <Checkbox value="VOUCHER">凭证</Checkbox>
              </CheckboxGroup>
            </div>
          </div>

          <!-- 第三行：状态和条件筛选 -->
          <div class="filter-row">
            <div class="filter-group">
              <label>归并状态</label>
              <Select v-model="searchForm.mergeStatus"
                      :datas="mergeStatusOptions"
                      style="width: 120px;">
              </Select>
            </div>
            <div class="filter-group">
              <label>关联状态</label>
              <Select v-model="searchForm.relationStatus"
                      :datas="relationStatusOptions"
                      style="width: 120px;">
              </Select>
            </div>
            <div class="filter-group">
              <label>AI处理状态</label>
              <Select v-model="searchForm.aiProcessStatus"
                      :datas="aiProcessStatusOptions"
                      style="width: 120px;">
              </Select>
            </div>
            <div class="filter-group">
              <label>日期范围</label>
              <DatePicker
                v-model="searchForm.dateRangeStr"
                type="daterange"
                placeholder="选择日期范围"
                style="width: 200px;">
              </DatePicker>
            </div>
            <div class="filter-group">
              <label>金额范围</label>
              <div class="amount-range">
                <Input v-model="searchForm.minAmount" placeholder="最小" style="width: 80px;" type="number"></Input>
                <span>-</span>
                <Input v-model="searchForm.maxAmount" placeholder="最大" style="width: 80px;" type="number"></Input>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 主数据区域 -->
    <div class="main-data-section">
      <!-- 左侧：数据展示区域 -->
      <div class="data-display-area">
        <!-- 视图切换和工具栏 -->
        <div class="display-toolbar">
          <div class="view-switcher">
            <ButtonGroup>
              <Button :type="currentView === 'list' ? 'primary' : 'default'"
                      @click="switchView('list')">
                <i class="h-icon-list"></i>
                列表视图
              </Button>
              <Button :type="currentView === 'graph' ? 'primary' : 'default'"
                      @click="switchView('graph')">
                <i class="h-icon-network"></i>
                关系图谱
              </Button>
            </ButtonGroup>
          </div>

          <div class="toolbar-actions">
            <Button @click="refreshData" :loading="refreshLoading">
              <i class="h-icon-refresh"></i>
              刷新
            </Button>
            <Button @click="exportData">
              <i class="h-icon-download"></i>
              导出
            </Button>
          </div>
        </div>

        <!-- 列表视图 -->
        <div v-show="currentView === 'list'" class="list-view">
          <div class="custom-table-wrapper">
            <table class="custom-table">
              <thead>
                <tr>
                  <th class="checkbox-col">
                    <input type="checkbox"
                           @change="selectAll"
                           :checked="isAllSelected"
                           :indeterminate="isIndeterminate">
                  </th>
                  <th class="type-col">类型</th>
                  <th class="no-col">编号</th>
                  <th class="summary-col">摘要/描述</th>
                  <th class="amount-col">金额</th>
                  <th class="date-col">日期</th>
                  <th class="status-col">状态</th>
                  <th class="action-col">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="tableLoading">
                  <td colspan="8" class="loading-row">
                    <div class="loading-content">
                      <i class="h-icon-loading"></i>
                      数据加载中...
                    </div>
                  </td>
                </tr>
                <tr v-else-if="!tableData || tableData.length === 0">
                  <td colspan="8" class="empty-row">
                    <div class="empty-content">
                      <i class="h-icon-inbox"></i>
                      暂无数据
                    </div>
                  </td>
                </tr>
                <tr v-else
                    v-for="(item, index) in tableData"
                    :key="item.id || index"
                    @click="onRowClick(item, index)"
                    :class="{ 'selected': selectedItems.includes(item.id) }"
                    class="data-row">
                  <td class="checkbox-col">
                    <input type="checkbox"
                           :checked="selectedItems.includes(item.id)"
                           @change="toggleSelection(item)"
                           @click.stop>
                  </td>
                  <td class="type-col">
                    <span class="entity-type-badge" :class="getEntityTypeClass(item.entity_type)">
                      {{ getEntityTypeLabel(item.entity_type) }}
                    </span>
                  </td>
                  <td class="no-col">
                    <span class="entity-no">{{ item.entity_no || '-' }}</span>
                  </td>
                  <td class="summary-col">
                    <div class="summary-content" :title="item.summary">
                      {{ item.summary || '-' }}
                    </div>
                  </td>
                  <td class="amount-col">
                    <span class="amount" v-if="item.amount">
                      ¥{{ formatAmount(item.amount) }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td class="date-col">
                    <span class="date">{{ formatDate(item.entity_date) }}</span>
                  </td>
                  <td class="status-col">
                    <div class="status-badges">
                      <span class="status-badge merge" :class="getMergeStatusClass(item.merge_status)">
                        {{ getMergeStatusLabel(item.merge_status) }}
                      </span>
                      <span class="status-badge relation" :class="getRelationStatusClass(item.relation_status)">
                        {{ getRelationStatusLabel(item.relation_status) }}
                      </span>
                    </div>
                  </td>
                  <td class="action-col">
                    <div class="action-buttons">
                      <button class="action-btn view" @click.stop="viewDetails(item)" title="查看详情">
                        <i class="h-icon-eye"></i>
                      </button>
                      <button class="action-btn edit" @click.stop="editItem(item)" title="编辑">
                        <i class="h-icon-edit"></i>
                      </button>
                      <button class="action-btn delete" @click.stop="deleteItem(item)" title="删除">
                        <i class="h-icon-delete"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 分页 -->
          <div class="pagination-container">
            <div class="pagination-info">
              共 {{ pagination.total }} 条记录，第 {{ pagination.current }} / {{ Math.ceil(pagination.total / pagination.pageSize) }} 页
            </div>
            <div class="pagination-controls">
              <button class="page-btn"
                      @click="goToPage(1)"
                      :disabled="pagination.current === 1">
                首页
              </button>
              <button class="page-btn"
                      @click="goToPage(pagination.current - 1)"
                      :disabled="pagination.current === 1">
                上一页
              </button>
              <span class="page-numbers">
                <button v-for="page in getPageNumbers()"
                        :key="page"
                        class="page-number"
                        :class="{ 'active': page === pagination.current }"
                        @click="goToPage(page)">
                  {{ page }}
                </button>
              </span>
              <button class="page-btn"
                      @click="goToPage(pagination.current + 1)"
                      :disabled="pagination.current >= Math.ceil(pagination.total / pagination.pageSize)">
                下一页
              </button>
              <button class="page-btn"
                      @click="goToPage(Math.ceil(pagination.total / pagination.pageSize))"
                      :disabled="pagination.current >= Math.ceil(pagination.total / pagination.pageSize)">
                末页
              </button>
              <select class="page-size-select" v-model="pagination.pageSize" @change="onPageSizeChange">
                <option value="10">10条/页</option>
                <option value="20">20条/页</option>
                <option value="50">50条/页</option>
                <option value="100">100条/页</option>
              </select>
            </div>
          </div>
        </div>

        <!-- 关系图谱视图 -->
        <div v-show="currentView === 'graph'" class="graph-view">
          <div class="graph-container" ref="graphContainer">
            <!-- 图谱将在这里渲染 -->
            <div class="graph-placeholder" v-if="!graphData.nodes || graphData.nodes.length === 0">
              <Icon type="ios-git-network" size="64" />
              <p>暂无关系图谱数据</p>
              <Button type="primary" @click="loadGraphData">加载关系图谱</Button>
            </div>
          </div>
          
          <div class="graph-controls">
            <ButtonGroup>
              <Button @click="zoomIn">
                <Icon type="ios-add" />
                放大
              </Button>
              <Button @click="zoomOut">
                <Icon type="ios-remove" />
                缩小
              </Button>
              <Button @click="resetZoom">
                <Icon type="ios-refresh" />
                重置
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </div>

      <!-- 右侧：详情面板 -->
      <div class="detail-panel" :class="{ 'collapsed': detailPanelCollapsed }">
        <div class="panel-header">
          <h3>详情信息</h3>
          <Button size="small" @click="toggleDetailPanel">
            <Icon :type="detailPanelCollapsed ? 'ios-arrow-back' : 'ios-arrow-forward'" />
          </Button>
        </div>

        <div class="detail-content" v-show="!detailPanelCollapsed">
          <div v-if="selectedItem" class="item-details">
            <!-- 基本信息 -->
            <div class="detail-section">
              <h4>基本信息</h4>
              <div class="detail-item">
                <span class="label">类型:</span>
                <span class="value">{{ getEntityTypeLabel(selectedItem.entityType) }}</span>
              </div>
              <div class="detail-item">
                <span class="label">编号:</span>
                <span class="value">{{ selectedItem.entityNo }}</span>
              </div>
              <div class="detail-item">
                <span class="label">金额:</span>
                <span class="value">¥{{ formatAmount(selectedItem.amount) }}</span>
              </div>
              <div class="detail-item">
                <span class="label">日期:</span>
                <span class="value">{{ formatDate(selectedItem.date) }}</span>
              </div>
            </div>

            <!-- 状态信息 -->
            <div class="detail-section">
              <h4>状态信息</h4>
              <div class="status-badges">
                <Tag :color="getStatusColor(selectedItem.mergeStatus)">
                  {{ selectedItem.mergeStatus === 'MERGED' ? '已归并' : '未归并' }}
                </Tag>
                <Tag :color="getStatusColor(selectedItem.relationStatus)">
                  {{ selectedItem.relationStatus === 'RELATED' ? '已关联' : '未关联' }}
                </Tag>
                <Tag :color="getStatusColor(selectedItem.aiProcessStatus)">
                  {{ getAiStatusLabel(selectedItem.aiProcessStatus) }}
                </Tag>
              </div>
            </div>

            <!-- 关联关系 -->
            <div class="detail-section" v-if="selectedItem.relations && selectedItem.relations.length > 0">
              <h4>关联关系 ({{ selectedItem.relations.length }})</h4>
              <div class="relation-list">
                <div v-for="relation in selectedItem.relations" :key="relation.relation_id" class="relation-item">
                  <div class="relation-header">
                    <span class="relation-type">{{ getRelationTypeLabel(relation.relation_type) }}</span>
                    <Tag :color="relation.creation_method === 'AI' ? 'purple' : 'blue'" size="small">
                      {{ relation.creation_method === 'AI' ? 'AI生成' : '手动创建' }}
                    </Tag>
                  </div>
                  <div class="relation-details">
                    <span class="relation-target">
                      {{ getEntityTypeLabel(relation.target_type) }}: {{ relation.target_id }}
                    </span>
                    <span class="relation-amount" v-if="relation.relation_amount">
                      金额: ¥{{ formatAmount(relation.relation_amount) }}
                    </span>
                  </div>
                  <div class="relation-time">
                    创建时间: {{ formatDateTime(relation.created_at) }}
                  </div>
                  <div class="relation-note" v-if="relation.relation_note">
                    备注: {{ relation.relation_note }}
                  </div>
                </div>
              </div>
            </div>

            <!-- 归并组信息 -->
            <div class="detail-section" v-if="selectedItem.group_info">
              <h4>归并组信息</h4>
              <div class="group-info">
                <div class="detail-item">
                  <span class="label">组名称:</span>
                  <span class="value">{{ selectedItem.group_info.group_name }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">组摘要:</span>
                  <span class="value">{{ selectedItem.group_info.group_summary }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">总金额:</span>
                  <span class="value">¥{{ formatAmount(selectedItem.group_info.total_amount) }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">项目数量:</span>
                  <span class="value">{{ selectedItem.group_info.item_count }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">创建时间:</span>
                  <span class="value">{{ formatDateTime(selectedItem.group_info.created_at) }}</span>
                </div>
                <div class="detail-item" v-if="selectedItem.group_info.merge_rule_id">
                  <span class="label">归并规则:</span>
                  <span class="value">{{ selectedItem.group_info.merge_rule_id }}</span>
                </div>
              </div>
            </div>

            <!-- AI处理历史 -->
            <div class="detail-section">
              <h4>AI处理历史</h4>
              <Button @click="loadAiHistory" :loading="aiHistoryLoading" size="small">
                <Icon type="ios-time" />
                查看历史
              </Button>
              <div v-if="aiHistory.length > 0" class="ai-history-list">
                <div v-for="history in aiHistory" :key="history.id" class="history-item">
                  <div class="history-time">{{ formatDateTime(history.processTime) }}</div>
                  <div class="history-action">{{ history.actionType }}</div>
                  <div class="history-result">{{ history.result }}</div>
                </div>
              </div>
            </div>

            <!-- 快速操作 -->
            <div class="detail-section">
              <h4>快速操作</h4>
              <div class="quick-actions">
                <Button type="primary" size="small" @click="startAiRelation" 
                        v-if="canStartAiRelation(selectedItem)">
                  <Icon type="ios-link" />
                  AI关联
                </Button>
                <Button type="primary" size="small" @click="startAiMerge"
                        v-if="canStartAiMerge(selectedItem)">
                  <Icon type="ios-copy" />
                  AI归并
                </Button>
                <Button type="primary" size="small" @click="generateVoucher"
                        v-if="canGenerateVoucher(selectedItem)">
                  <Icon type="ios-document" />
                  生成凭证
                </Button>
              </div>
            </div>
          </div>

          <div v-else class="no-selection">
            <Icon type="ios-information-circle" size="48" />
            <p>请选择一个项目查看详情</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 主数据区域结束 -->

    <!-- 底部快速操作工具栏 -->
    <div class="quick-toolbar">
      <div class="selected-info" v-if="selectedItems.length > 0">
        <span>已选择 {{ selectedItems.length }} 项</span>
      </div>
      
      <div class="toolbar-actions">
        <Button type="primary" @click="batchAiRelation" 
                :disabled="selectedItems.length === 0">
          <Icon type="ios-link" />
          批量AI关联
        </Button>
        <Button type="primary" @click="batchAiMerge"
                :disabled="selectedItems.length === 0">
          <Icon type="ios-copy" />
          批量AI归并
        </Button>
        <Button type="primary" @click="batchGenerateVoucher"
                :disabled="selectedItems.length === 0">
          <Icon type="ios-document" />
          批量生成凭证
        </Button>
        <Button @click="navigateToAiDashboard">
          <Icon type="ios-settings" />
          AI设置
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
// import unifiedQueryApi from '@/api/unified-query'

export default {
  name: 'UnifiedQueryNew',
  data() {
    return {
      // 统计数据
      statistics: {
        documentStats: {
          totalCount: 0,
          mergedCount: 0,
          relatedCount: 0
        },
        receiptStats: {
          totalCount: 0,
          mergedCount: 0,
          relatedCount: 0
        },
        mergeGroupStats: {
          documentGroupCount: 0,
          receiptGroupCount: 0
        },
        relationStats: {
          totalRelationCount: 0,
          aiGeneratedRelationCount: 0,
          manualRelationCount: 0
        },
        voucherStats: {
          totalVoucherCount: 0,
          aiGeneratedVoucherCount: 0,
          pendingVoucherDocumentCount: 0,
          pendingVoucherReceiptCount: 0
        },
        aiProcessStats: {
          runningAiTaskCount: 0,
          completedAiTaskCount: 0,
          aiServiceAvailable: false
        }
      },

      // 面板状态
      searchPanelCollapsed: false,
      detailPanelCollapsed: false,

      // 当前视图
      currentView: 'list', // 'list' 或 'graph'

      // 下拉选项数据
      mergeStatusOptions: [
        { key: 'ALL', title: '全部' },
        { key: 'MERGED', title: '已归并' },
        { key: 'UNMERGED', title: '未归并' }
      ],
      relationStatusOptions: [
        { key: 'ALL', title: '全部' },
        { key: 'RELATED', title: '已关联' },
        { key: 'UNRELATED', title: '未关联' }
      ],
      aiProcessStatusOptions: [
        { key: 'ALL', title: '全部' },
        { key: 'PROCESSED', title: '已处理' },
        { key: 'PENDING', title: '待处理' }
      ],
      
      // 搜索表单
      searchForm: {
        keyword: '',
        entityTypes: ['DOCUMENT', 'RECEIPT'],
        mergeStatus: 'ALL',
        relationStatus: 'ALL',
        aiProcessStatus: 'ALL',
        dateRange: [],
        dateRangeStr: '',
        minAmount: null,
        maxAmount: null
      },

      // 加载状态
      searchLoading: false,
      tableLoading: false,
      refreshLoading: false,
      aiHistoryLoading: false,

      // 表格数据
      tableData: [],
      tableColumns: [
        {
          type: 'selection',
          width: 60,
          align: 'center'
        },
        {
          title: '类型',
          key: 'entity_type',
          width: 80,
          render: (h, params) => {
            return h('span', this.getEntityTypeLabel(params.row.entity_type))
          }
        },
        {
          title: '编号',
          key: 'entity_no',
          width: 150
        },
        {
          title: '摘要/描述',
          key: 'summary',
          ellipsis: true
        },
        {
          title: '金额',
          key: 'amount',
          width: 120,
          render: (h, params) => {
            return h('span', '¥' + this.formatAmount(params.row.amount))
          }
        },
        {
          title: '日期',
          key: 'entity_date',
          width: 120,
          render: (h, params) => {
            return h('span', this.formatDate(params.row.entity_date))
          }
        },
        {
          title: '状态',
          key: 'status',
          width: 250,
          render: (h, params) => {
            const tags = []

            // 归并状态
            if (params.row.merge_status === 'MERGED') {
              const mergeTag = h('Tag', {
                props: { color: 'green' },
                attrs: { title: '已归并到组' }
              }, '已归并')
              tags.push(mergeTag)
            } else {
              const unmergeTag = h('Tag', {
                props: { color: 'default' },
                attrs: { title: '未归并' }
              }, '未归并')
              tags.push(unmergeTag)
            }

            // 关联状态
            if (params.row.relation_status === 'RELATED') {
              const relationTag = h('Tag', {
                props: { color: 'blue' },
                attrs: { title: '已建立关联关系' }
              }, `已关联(${params.row.relation_count || 0})`)
              tags.push(relationTag)
            } else {
              const unrelatedTag = h('Tag', {
                props: { color: 'default' },
                attrs: { title: '未建立关联关系' }
              }, '未关联')
              tags.push(unrelatedTag)
            }

            // AI处理状态
            if (params.row.ai_process_status === 'PROCESSED') {
              const aiTag = h('Tag', {
                props: { color: 'purple' },
                attrs: { title: 'AI已处理' }
              }, 'AI已处理')
              tags.push(aiTag)
            } else if (params.row.ai_process_status === 'PENDING') {
              const pendingTag = h('Tag', {
                props: { color: 'orange' },
                attrs: { title: 'AI待处理' }
              }, 'AI待处理')
              tags.push(pendingTag)
            }

            return h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '4px' } }, tags)
          }
        },
        {
          title: '操作',
          key: 'action',
          width: 150,
          render: (h, params) => {
            const buttons = []

            // 查看详情按钮
            buttons.push(h('Button', {
              props: { type: 'primary', size: 'small' },
              style: { marginRight: '4px', marginBottom: '4px' },
              on: {
                click: () => {
                  this.viewEntityDetails(params.row)
                }
              }
            }, '详情'))

            // 根据状态显示不同操作
            if (params.row.merge_status === 'UNMERGED' && ['DOCUMENT', 'RECEIPT'].includes(params.row.entity_type)) {
              buttons.push(h('Button', {
                props: { type: 'default', size: 'small' },
                style: { marginRight: '4px', marginBottom: '4px' },
                on: {
                  click: () => {
                    this.startMergeForEntity(params.row)
                  }
                }
              }, '归并'))
            }

            if (params.row.relation_status === 'UNRELATED' && ['DOCUMENT', 'RECEIPT'].includes(params.row.entity_type)) {
              buttons.push(h('Button', {
                props: { type: 'default', size: 'small' },
                style: { marginBottom: '4px' },
                on: {
                  click: () => {
                    this.startRelationForEntity(params.row)
                  }
                }
              }, '关联'))
            }

            return h('div', { style: { display: 'flex', flexWrap: 'wrap' } }, buttons)
          }
        }
      ],

      // 分页
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0
      },

      // 选中项
      selectedItems: [],
      selectedItem: null,

      // 关系图谱数据
      graphData: {
        nodes: [],
        edges: []
      },

      // AI历史数据
      aiHistory: []
    }
  },

  computed: {
    // 表格选择状态
    isAllSelected() {
      return this.tableData.length > 0 && this.selectedItems.length === this.tableData.length
    },

    isIndeterminate() {
      return this.selectedItems.length > 0 && this.selectedItems.length < this.tableData.length
    }
  },

  mounted() {
    this.loadStatistics()
    this.executeSearch()
  },

  methods: {
    // 加载统计数据
    async loadStatistics() {
      try {
        const response = await fetch('/api/unified-query/statistics', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const result = await response.json()
          if (result && result.success) {
            this.statistics = result.data || this.getDefaultStatistics()
          } else {
            console.warn('统计数据加载失败:', result)
            this.statistics = this.getDefaultStatistics()
          }
        } else {
          console.warn('统计数据请求失败:', response.status)
          this.statistics = this.getDefaultStatistics()
        }
      } catch (error) {
        console.error('加载统计数据失败:', error)
        this.statistics = this.getDefaultStatistics()
      }
    },

    // 获取默认统计数据
    getDefaultStatistics() {
      return {
        documentStats: {
          totalCount: 0,
          mergedCount: 0,
          relatedCount: 0,
          totalAmount: 0
        },
        receiptStats: {
          totalCount: 0,
          mergedCount: 0,
          relatedCount: 0,
          totalAmount: 0
        },
        mergeGroupStats: {
          documentGroupCount: 0,
          receiptGroupCount: 0
        },
        relationStats: {
          totalRelationCount: 0,
          aiGeneratedRelationCount: 0,
          manualRelationCount: 0
        },
        voucherStats: {
          totalVoucherCount: 0,
          aiGeneratedVoucherCount: 0,
          pendingVoucherDocumentCount: 0,
          pendingVoucherReceiptCount: 0
        },
        aiProcessStats: {
          runningAiTaskCount: 0,
          completedAiTaskCount: 0,
          aiServiceAvailable: false
        }
      }
    },

    // 执行搜索
    async executeSearch() {
      this.searchLoading = true
      this.tableLoading = true

      try {
        const queryData = this.buildQueryData()
        const response = await fetch('/api/unified-query/all-entities', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(queryData)
        })

        if (response.ok) {
          const result = await response.json()
          if (result && result.success) {
            this.tableData = result.data && result.data.records ? result.data.records : []
            this.pagination.total = result.data && result.data.total ? result.data.total : 0
            console.log('查询成功:', this.tableData.length, '条记录')
          } else {
            console.warn('查询失败:', result)
            this.tableData = []
            this.pagination.total = 0
          }
        } else {
          console.warn('查询请求失败:', response.status)
          this.tableData = []
          this.pagination.total = 0
        }
      } catch (error) {
        console.error('查询失败:', error)
        this.tableData = []
        this.pagination.total = 0
      } finally {
        this.searchLoading = false
        this.tableLoading = false
      }
    },

    // 构建查询数据
    buildQueryData() {
      const condition = {
        keyword: this.searchForm.keyword,
        entityTypes: this.searchForm.entityTypes,
        mergeStatus: this.searchForm.mergeStatus,
        relationStatus: this.searchForm.relationStatus,
        aiProcessStatus: this.searchForm.aiProcessStatus,
        minAmount: this.searchForm.minAmount,
        maxAmount: this.searchForm.maxAmount,
        includeRelations: true,
        includeAiHistory: false,
        includeStatistics: false
      }

      if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
        condition.startDate = this.searchForm.dateRange[0]
        condition.endDate = this.searchForm.dateRange[1]
      }

      return {
        condition,
        pageParam: {
          pageNum: this.pagination.current,
          pageSize: this.pagination.pageSize,
          orderBy: 'created_at',
          orderDirection: 'DESC'
        }
      }
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        keyword: '',
        entityTypes: ['DOCUMENT', 'RECEIPT'],
        mergeStatus: 'ALL',
        relationStatus: 'ALL',
        aiProcessStatus: 'ALL',
        dateRange: [],
        minAmount: null,
        maxAmount: null
      }
    },

    // 重置搜索并执行
    resetAndSearch() {
      this.resetSearch()
      this.executeSearch()
    },

    // 切换视图
    switchView(view) {
      this.currentView = view
      if (view === 'graph') {
        this.loadGraphData()
      }
    },

    // 加载关系图谱数据
    async loadGraphData() {
      try {
        const queryData = this.buildQueryData()
        const response = await fetch('/api/unified-query/relationship-graph', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(queryData)
        })

        if (response.ok) {
          const result = await response.json()
          if (result && result.success) {
            this.graphData = result.data || { nodes: [], edges: [] }
            this.$nextTick(() => {
              this.renderGraph()
            })
          } else {
            console.warn('加载关系图谱失败:', result)
            this.graphData = { nodes: [], edges: [] }
          }
        } else {
          console.warn('关系图谱请求失败:', response.status)
          this.graphData = { nodes: [], edges: [] }
        }
      } catch (error) {
        console.error('加载关系图谱失败:', error)
        this.graphData = { nodes: [], edges: [] }
      }
    },

    // 渲染关系图谱
    renderGraph() {
      // TODO: 使用D3.js或ECharts渲染图谱
      console.log('渲染关系图谱:', this.graphData)
    },

    // 表格行点击
    onRowClick(row) {
      this.selectedItem = row
    },

    // 表格选择变化
    onSelectionChange(selection) {
      this.selectedItems = selection
    },

    // 分页变化
    onPageChange(page) {
      this.pagination.current = page
      this.executeSearch()
    },

    onPageSizeChange(event) {
      this.pagination.pageSize = parseInt(event.target.value)
      this.pagination.current = 1
      this.executeSearch()
    },

    // 实体操作方法
    viewEntityDetails(entity) {
      this.selectedItem = entity
      // 加载详细信息，包括关联关系
      this.loadEntityDetails(entity)
    },

    async loadEntityDetails(entity) {
      try {
        // 加载关联关系
        const relationResponse = await fetch(`/api/unified-query/ai-history/${entity.entity_type}/${entity.entity_id}`, {
          method: 'GET',
          credentials: 'include'
        })

        if (relationResponse.ok) {
          const result = await relationResponse.json()
          if (result.success && result.data) {
            entity.relations = result.data
          }
        }

        // 如果有归并组，加载组信息
        if (entity.group_id) {
          // 组信息已经在后端查询中包含了
        }

        this.selectedItem = { ...entity }
      } catch (error) {
        console.error('加载实体详情失败:', error)
      }
    },

    startMergeForEntity(entity) {
      this.$Message.info(`启动 ${this.getEntityTypeLabel(entity.entity_type)} 归并...`)
      // 跳转到归并页面或打开归并对话框
      if (entity.entity_type === 'DOCUMENT') {
        this.$router.push('/ai-merge-engine?type=documents&id=' + entity.entity_id)
      } else if (entity.entity_type === 'RECEIPT') {
        this.$router.push('/ai-merge-engine?type=receipts&id=' + entity.entity_id)
      }
    },

    startRelationForEntity(entity) {
      this.$Message.info(`启动 ${this.getEntityTypeLabel(entity.entity_type)} 关联...`)
      // 跳转到关联页面或打开关联对话框
      this.$router.push('/ai-relation-engine?type=' + entity.entity_type.toLowerCase() + '&id=' + entity.entity_id)
    },

    // 工具方法
    getEntityTypeLabel(type) {
      const labels = {
        'DOCUMENT': '票据',
        'RECEIPT': '银证',
        'DOCUMENT_GROUP': '票据组',
        'RECEIPT_GROUP': '银证组',
        'VOUCHER': '凭证'
      }
      return labels[type] || type
    },

    getRelationTypeLabel(type) {
      const labels = {
        'AMOUNT_MATCH': '金额匹配',
        'PARTIAL_MATCH': '部分匹配',
        'MERGE_RELATION': '归并关系',
        'VOUCHER_RELATION': '凭证关联',
        'MANUAL_RELATION': '手动关联',
        'AI_RELATION': 'AI关联'
      }
      return labels[type] || type
    },

    formatAmount(amount) {
      return amount ? Number(amount).toLocaleString() : '0'
    },

    formatDate(date) {
      return date ? new Date(date).toLocaleDateString() : ''
    },

    formatDateTime(datetime) {
      return datetime ? new Date(datetime).toLocaleString() : ''
    },

    getStatusColor(status) {
      const colors = {
        'MERGED': 'green',
        'RELATED': 'blue',
        'PROCESSED': 'purple',
        'PENDING': 'orange',
        'FAILED': 'red'
      }
      return colors[status] || 'default'
    },

    getAiStatusLabel(status) {
      const labels = {
        'PROCESSED': 'AI已处理',
        'PENDING': 'AI待处理',
        'FAILED': 'AI处理失败'
      }
      return labels[status] || status
    },

    // 面板切换
    toggleSearchPanel() {
      this.searchPanelCollapsed = !this.searchPanelCollapsed
    },

    toggleDetailPanel() {
      this.detailPanelCollapsed = !this.detailPanelCollapsed
    },

    // 快速筛选
    filterByType(type) {
      // 重置所有筛选条件
      this.resetSearch()

      switch (type) {
        case 'documents':
          this.searchForm.entityTypes = ['DOCUMENT']
          this.$Message.info('已筛选票据数据')
          break
        case 'receipts':
          this.searchForm.entityTypes = ['RECEIPT']
          this.$Message.info('已筛选银行回单数据')
          break
        case 'merge-groups':
          this.searchForm.entityTypes = ['DOCUMENT_GROUP', 'RECEIPT_GROUP']
          this.$Message.info('已筛选归并组数据')
          break
        case 'vouchers':
          this.searchForm.entityTypes = ['VOUCHER']
          this.$Message.info('已筛选会计凭证数据')
          break
      }
      this.executeSearch()
    },

    // 按状态筛选
    filterByStatus(status) {
      // 重置所有筛选条件
      this.resetSearch()

      switch (status) {
        case 'related':
          this.searchForm.relationStatus = 'RELATED'
          this.searchForm.entityTypes = ['DOCUMENT', 'RECEIPT']
          this.$Message.info('已筛选已关联数据')
          break
        case 'merged':
          this.searchForm.mergeStatus = 'MERGED'
          this.searchForm.entityTypes = ['DOCUMENT', 'RECEIPT']
          this.$Message.info('已筛选已归并数据')
          break
        case 'ai-processed':
          this.searchForm.aiProcessStatus = 'PROCESSED'
          this.searchForm.entityTypes = ['DOCUMENT', 'RECEIPT']
          this.$Message.info('已筛选AI已处理数据')
          break
      }
      this.executeSearch()
    },

    // 判断卡片是否激活
    isCardActive(type) {
      switch (type) {
        case 'documents':
          return this.searchForm.entityTypes.length === 1 && this.searchForm.entityTypes.includes('DOCUMENT')
        case 'receipts':
          return this.searchForm.entityTypes.length === 1 && this.searchForm.entityTypes.includes('RECEIPT')
        case 'merge-groups':
          return this.searchForm.entityTypes.length === 2 &&
                 this.searchForm.entityTypes.includes('DOCUMENT_GROUP') &&
                 this.searchForm.entityTypes.includes('RECEIPT_GROUP')
        case 'vouchers':
          return this.searchForm.entityTypes.length === 1 && this.searchForm.entityTypes.includes('VOUCHER')
        default:
          return false
      }
    },

    // 判断状态筛选是否激活
    isStatusActive(status) {
      switch (status) {
        case 'related':
          return this.searchForm.relationStatus === 'RELATED'
        case 'merged':
          return this.searchForm.mergeStatus === 'MERGED'
        case 'ai-processed':
          return this.searchForm.aiProcessStatus === 'PROCESSED'
        default:
          return false
      }
    },

    // 显示全部数据
    showAllData() {
      this.resetSearch()
      this.searchForm.entityTypes = ['DOCUMENT', 'RECEIPT', 'DOCUMENT_GROUP', 'RECEIPT_GROUP', 'VOUCHER']
      this.$Message.info('已显示所有类型数据')
      this.executeSearch()
    },

    // 判断是否显示全部数据
    isShowingAll() {
      const allTypes = ['DOCUMENT', 'RECEIPT', 'DOCUMENT_GROUP', 'RECEIPT_GROUP', 'VOUCHER']
      return this.searchForm.entityTypes.length === allTypes.length &&
             allTypes.every(type => this.searchForm.entityTypes.includes(type)) &&
             this.searchForm.mergeStatus === 'ALL' &&
             this.searchForm.relationStatus === 'ALL' &&
             this.searchForm.aiProcessStatus === 'ALL' &&
             !this.searchForm.keyword &&
             (!this.searchForm.dateRange || this.searchForm.dateRange.length === 0) &&
             !this.searchForm.minAmount &&
             !this.searchForm.maxAmount
    },

    // 保存搜索方案
    saveSearchScheme() {
      this.$Message.info('搜索方案保存功能开发中...')
    },

    // 表格选择相关方法
    selectAll(event) {
      if (event.target.checked) {
        this.selectedItems = this.tableData.map(item => item.id)
      } else {
        this.selectedItems = []
      }
    },

    toggleSelection(item) {
      const index = this.selectedItems.indexOf(item.id)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      } else {
        this.selectedItems.push(item.id)
      }
    },

    // 分页相关方法
    goToPage(page) {
      if (page >= 1 && page <= Math.ceil(this.pagination.total / this.pagination.pageSize)) {
        this.pagination.current = page
        this.executeSearch()
      }
    },

    getPageNumbers() {
      const total = Math.ceil(this.pagination.total / this.pagination.pageSize)
      const current = this.pagination.current
      const pages = []

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        } else if (current >= total - 3) {
          pages.push(1)
          pages.push('...')
          for (let i = total - 4; i <= total; i++) {
            pages.push(i)
          }
        } else {
          pages.push(1)
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        }
      }

      return pages
    },

    // 操作按钮方法
    viewDetails(item) {
      this.selectedItem = item
      this.detailPanelCollapsed = false
    },

    editItem(item) {
      this.$Message.info('编辑功能开发中...')
    },

    deleteItem(item) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除这条记录吗？`,
        onOk: () => {
          this.$Message.info('删除功能开发中...')
        }
      })
    },

    // 实体类型相关方法
    getEntityTypeClass(entityType) {
      const classMap = {
        'DOCUMENT': 'document',
        'RECEIPT': 'receipt',
        'DOCUMENT_GROUP': 'document-group',
        'RECEIPT_GROUP': 'receipt-group',
        'VOUCHER': 'voucher'
      }
      return classMap[entityType] || 'default'
    },

    // 状态相关方法
    getMergeStatusClass(status) {
      return status === 'MERGED' ? 'merged' : 'unmerged'
    },

    getMergeStatusLabel(status) {
      return status === 'MERGED' ? '已归并' : '未归并'
    },

    getRelationStatusClass(status) {
      return status === 'RELATED' ? 'related' : 'unrelated'
    },

    getRelationStatusLabel(status) {
      return status === 'RELATED' ? '已关联' : '未关联'
    },

    // AI操作判断
    canStartAiRelation(item) {
      return item && ['DOCUMENT', 'RECEIPT'].includes(item.entityType) && item.relationStatus !== 'RELATED'
    },

    canStartAiMerge(item) {
      return item && ['DOCUMENT', 'RECEIPT'].includes(item.entityType) && item.mergeStatus !== 'MERGED'
    },

    canGenerateVoucher(item) {
      return item && ['DOCUMENT', 'RECEIPT', 'DOCUMENT_GROUP', 'RECEIPT_GROUP'].includes(item.entityType)
    },

    // AI操作
    async startAiRelation() {
      this.$Message.info('启动AI智能关联...')
      // TODO: 实现AI关联逻辑
    },

    async startAiMerge() {
      this.$Message.info('启动AI智能归并...')
      // TODO: 实现AI归并逻辑
    },

    async generateVoucher() {
      this.$Message.info('生成会计凭证...')
      // TODO: 实现凭证生成逻辑
    },

    // 批量操作
    async batchAiRelation() {
      if (this.selectedItems.length === 0) {
        this.$Message.warning('请先选择要处理的项目')
        return
      }

      try {
        this.$Message.info(`开始批量AI关联 ${this.selectedItems.length} 项...`)

        // 分离票据和银证
        const bills = this.selectedItems.filter(item => item.entity_type === 'DOCUMENT')
        const receipts = this.selectedItems.filter(item => item.entity_type === 'RECEIPT')

        if (bills.length === 0 && receipts.length === 0) {
          this.$Message.warning('只能对票据和银证进行关联操作')
          return
        }

        // 调用真实的批量AI关联API
        const response = await fetch('/api/ai-relation/batch-analyze', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            bills: bills.map(item => ({ id: item.entity_id })),
            receipts: receipts.map(item => ({ id: item.entity_id }))
          })
        })

        let result = { success: false }
        if (response.ok) {
          result = await response.json()
        }

        if (result.success) {
          this.$Message.success('批量AI关联启动成功')
          this.executeSearch() // 刷新数据
          this.loadStatistics() // 刷新统计
        } else {
          this.$Message.error('批量AI关联失败: ' + result.msg)
        }
      } catch (error) {
        this.$Message.error('批量AI关联失败: ' + error.message)
        console.error('批量AI关联失败:', error)
      }
    },

    async batchAiMerge() {
      if (this.selectedItems.length === 0) {
        this.$Message.warning('请先选择要处理的项目')
        return
      }

      try {
        this.$Message.info(`开始批量AI归并 ${this.selectedItems.length} 项...`)

        // 分离票据和银证
        const documents = this.selectedItems.filter(item => item.entity_type === 'DOCUMENT')
        const receipts = this.selectedItems.filter(item => item.entity_type === 'RECEIPT')

        if (documents.length === 0 && receipts.length === 0) {
          this.$Message.warning('只能对票据和银证进行归并操作')
          return
        }

        const promises = []

        // 调用真实的批量AI归并API
        if (documents.length > 0) {
          const docPromise = fetch('/api/ai-merge/batch-analyze', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              type: 'documents',
              entities: documents.map(item => ({ id: item.entity_id }))
            })
          }).then(response => response.ok ? response.json() : { success: false })
          promises.push(docPromise)
        }

        if (receipts.length > 0) {
          const receiptPromise = fetch('/api/ai-merge/batch-analyze', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              type: 'receipts',
              entities: receipts.map(item => ({ id: item.entity_id }))
            })
          }).then(response => response.ok ? response.json() : { success: false })
          promises.push(receiptPromise)
        }

        const results = await Promise.all(promises)
        const allSuccess = results.every(result => result.success)

        if (allSuccess) {
          this.$Message.success('批量AI归并启动成功')
          this.executeSearch() // 刷新数据
          this.loadStatistics() // 刷新统计
        } else {
          this.$Message.error('部分批量AI归并失败')
        }
      } catch (error) {
        this.$Message.error('批量AI归并失败: ' + error.message)
        console.error('批量AI归并失败:', error)
      }
    },

    async batchGenerateVoucher() {
      if (this.selectedItems.length === 0) {
        this.$Message.warning('请先选择要处理的项目')
        return
      }

      try {
        this.$Message.info(`开始批量生成凭证 ${this.selectedItems.length} 项...`)

        const validItems = this.selectedItems.filter(item =>
          ['DOCUMENT', 'RECEIPT', 'DOCUMENT_GROUP', 'RECEIPT_GROUP'].includes(item.entity_type)
        )

        if (validItems.length === 0) {
          this.$Message.warning('选择的项目不支持凭证生成')
          return
        }

        // 调用真实的批量凭证生成API
        const response = await fetch('/api/ai-accounting/batch-generate-voucher', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            entities: validItems.map(item => ({
              id: item.entity_id,
              type: item.entity_type
            }))
          })
        })

        let result = { success: false }
        if (response.ok) {
          result = await response.json()
        }

        if (result.success) {
          this.$Message.success('批量凭证生成启动成功')
          this.executeSearch() // 刷新数据
          this.loadStatistics() // 刷新统计
        } else {
          this.$Message.error('批量凭证生成失败: ' + result.msg)
        }
      } catch (error) {
        this.$Message.error('批量凭证生成失败: ' + error.message)
        console.error('批量凭证生成失败:', error)
      }
    },

    // 加载AI历史
    async loadAiHistory() {
      if (!this.selectedItem) return

      this.aiHistoryLoading = true
      try {
        const response = await fetch(`/api/unified-query/ai-history/${this.selectedItem.entity_type}/${this.selectedItem.entity_id}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const result = await response.json()
          if (result && result.success) {
            this.aiHistory = result.data || []
          } else {
            console.warn('加载AI历史失败:', result)
            this.aiHistory = []
          }
        } else {
          console.warn('AI历史请求失败:', response.status)
          this.aiHistory = []
        }
      } catch (error) {
        console.error('加载AI历史失败:', error)
        this.aiHistory = []
      } finally {
        this.aiHistoryLoading = false
      }
    },

    // 其他操作
    refreshData() {
      this.refreshLoading = true
      Promise.all([
        this.loadStatistics(),
        this.executeSearch()
      ]).finally(() => {
        this.refreshLoading = false
      })
    },

    async exportData() {
      try {
        this.$Message.info('正在导出数据...')

        // 暂时模拟导出功能
        const queryData = this.buildQueryData()
        console.log('导出数据:', queryData)

        // 创建一个简单的CSV内容
        const csvContent = this.generateCSV()
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `AI财务数据查询结果_${new Date().toISOString().slice(0, 10)}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$Message.success('数据导出成功')
      } catch (error) {
        console.error('导出数据失败:', error)
        this.$Message.error('导出数据失败: ' + error.message)
      }
    },

    generateCSV() {
      const headers = ['类型', '编号', '摘要', '金额', '日期', '状态']
      const rows = this.tableData.map(item => [
        this.getEntityTypeLabel(item.entity_type),
        item.entity_no || '',
        item.summary || '',
        item.amount || 0,
        this.formatDate(item.entity_date),
        `${item.merge_status === 'MERGED' ? '已归并' : '未归并'} ${item.relation_status === 'RELATED' ? '已关联' : '未关联'}`
      ])

      const csvContent = [headers, ...rows]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n')

      return '\uFEFF' + csvContent // 添加BOM以支持中文
    },

    saveSearchScheme() {
      // 保存当前搜索条件到本地存储
      const scheme = {
        name: `搜索方案_${new Date().toLocaleString()}`,
        conditions: { ...this.searchForm },
        dateRange: [...this.searchForm.dateRange]
      }

      let savedSchemes = JSON.parse(localStorage.getItem('unifiedQuerySchemes') || '[]')
      savedSchemes.push(scheme)

      // 最多保存10个方案
      if (savedSchemes.length > 10) {
        savedSchemes = savedSchemes.slice(-10)
      }

      localStorage.setItem('unifiedQuerySchemes', JSON.stringify(savedSchemes))
      this.$Message.success('搜索方案已保存')
    },

    loadSearchSchemes() {
      // 加载保存的搜索方案
      const savedSchemes = JSON.parse(localStorage.getItem('unifiedQuerySchemes') || '[]')
      return savedSchemes
    },

    applySearchScheme(scheme) {
      // 应用保存的搜索方案
      this.searchForm = { ...scheme.conditions }
      this.searchForm.dateRange = [...scheme.dateRange]
      this.executeSearch()
      this.$Message.success('搜索方案已应用')
    },

    navigateToAiDashboard() {
      this.$router.push('/ai-dashboard')
    },



    // 图谱控制
    zoomIn() {
      // TODO: 实现图谱放大
    },

    zoomOut() {
      // TODO: 实现图谱缩小
    },

    resetZoom() {
      // TODO: 实现图谱重置
    }
  }
}
</script>

<style scoped>
.unified-query-new {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 60px);
}

/* 智能仪表板 */
.dashboard-section {
  margin-bottom: 20px;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
}

.dashboard-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.stat-card.active {
  border: 2px solid #667eea;
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f0ff 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
}

.stat-card.active::before {
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  height: 6px;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

.stat-card .card-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.stat-card h3 {
  margin: 0 0 8px 0;
  font-size: 16px;
  color: #666;
  font-weight: 500;
}

.stat-number {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
}

.stat-details {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #999;
}

.card-filter-hint {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 10px;
  color: #999;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stat-card:hover .card-filter-hint {
  opacity: 1;
}

.stat-card.active .card-filter-hint {
  opacity: 1;
  color: #667eea;
  font-weight: 500;
}

.ai-status-card {
  position: relative;
}

.ai-status-indicator {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #e74c3c;
}

.ai-status-indicator.online {
  background: #27ae60;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

/* 智能搜索筛选区域 */
.search-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
}

.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f0ff 100%);
  border-bottom: 1px solid #e8e8e8;
}

.search-header h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.search-content {
  padding: 20px;
}

.search-form-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.search-input-group {
  flex: 1;
}

.search-actions {
  display: flex;
  gap: 8px;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-group label {
  font-size: 14px;
  color: #666;
  white-space: nowrap;
  min-width: 60px;
}

.amount-range {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 主数据区域 */
.main-data-section {
  display: flex;
  gap: 20px;
  min-height: 600px;
  margin-bottom: 100px; /* 为底部工具栏留出空间 */
}



.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
}

.panel-header h3 {
  margin: 0;
  font-size: 16px;
  color: #2c3e50;
}



.status-filters .ivu-select {
  margin-bottom: 8px;
}

.amount-range {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 数据展示区 */
.data-display-area {
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  min-height: 600px;
}

.display-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
  border-radius: 12px 12px 0 0;
}

.list-view {
  flex: 1;
  padding: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.list-view .ivu-table-wrapper {
  flex: 1;
}

.pagination-wrapper {
  margin-top: 16px;
  text-align: center;
}

.graph-view {
  flex: 1;
  position: relative;
  padding: 16px;
}

.graph-container {
  width: 100%;
  height: 100%;
  border: 1px solid #eee;
  border-radius: 8px;
  position: relative;
}

.graph-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
}

.graph-placeholder p {
  margin: 16px 0;
  font-size: 16px;
}

.graph-controls {
  position: absolute;
  top: 24px;
  right: 24px;
}

/* 详情面板 */
.detail-panel {
  width: 380px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease;
  overflow: hidden;
  height: fit-content;
  max-height: 600px;
}

.detail-panel.collapsed {
  width: 60px;
}

.detail-content {
  padding: 16px;
  max-height: calc(100vh - 400px);
  overflow-y: auto;
}

.item-details {
  font-size: 14px;
}

.detail-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.detail-section:last-child {
  border-bottom: none;
}

.detail-section h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  color: #2c3e50;
  font-weight: 600;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.detail-item .label {
  color: #666;
  font-weight: 500;
}

.detail-item .value {
  color: #2c3e50;
  font-weight: 600;
}

.status-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.relation-list {
  max-height: 150px;
  overflow-y: auto;
}

.relation-item {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 12px;
  border-left: 4px solid #667eea;
}

.relation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.relation-type {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.relation-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 6px;
}

.relation-target {
  color: #667eea;
  font-weight: 500;
  font-size: 13px;
}

.relation-amount {
  color: #27ae60;
  font-weight: 500;
  font-size: 13px;
}

.relation-time {
  color: #7f8c8d;
  font-size: 12px;
  margin-bottom: 4px;
}

.relation-note {
  color: #5a6c7d;
  font-size: 12px;
  font-style: italic;
  background: #ecf0f1;
  padding: 4px 8px;
  border-radius: 4px;
}

.group-info {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid #9b59b6;
}

.ai-history-list {
  max-height: 200px;
  overflow-y: auto;
  margin-top: 12px;
}

.history-item {
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 12px;
}

.history-time {
  color: #999;
  margin-bottom: 4px;
}

.history-action {
  font-weight: 500;
  color: #2c3e50;
}

.history-result {
  color: #666;
  margin-top: 4px;
}

.quick-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.no-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #999;
}

.no-selection p {
  margin-top: 16px;
  font-size: 14px;
}

/* 原生表格样式 */
.custom-table-wrapper {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.custom-table th {
  background: #f8f9fa;
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.custom-table td {
  padding: 12px 8px;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;
}

.custom-table .data-row:hover {
  background-color: #f8f9fa;
}

.custom-table .data-row.selected {
  background-color: #e3f2fd;
}

/* 列宽设置 */
.checkbox-col { width: 50px; text-align: center; }
.type-col { width: 80px; }
.no-col { width: 150px; }
.summary-col { min-width: 200px; }
.amount-col { width: 120px; text-align: right; }
.date-col { width: 120px; }
.status-col { width: 150px; }
.action-col { width: 120px; text-align: center; }

/* 实体类型徽章 */
.entity-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: white;
}

.entity-type-badge.document { background-color: #007bff; }
.entity-type-badge.receipt { background-color: #28a745; }
.entity-type-badge.document-group { background-color: #6f42c1; }
.entity-type-badge.receipt-group { background-color: #20c997; }
.entity-type-badge.voucher { background-color: #fd7e14; }
.entity-type-badge.default { background-color: #6c757d; }

/* 状态徽章 */
.status-badges {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
}

.status-badge.merge.merged { background-color: #d4edda; color: #155724; }
.status-badge.merge.unmerged { background-color: #f8d7da; color: #721c24; }
.status-badge.relation.related { background-color: #d1ecf1; color: #0c5460; }
.status-badge.relation.unrelated { background-color: #ffeaa7; color: #856404; }

/* 操作按钮 */
.action-buttons {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.action-btn {
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.action-btn.view { background-color: #007bff; color: white; }
.action-btn.edit { background-color: #28a745; color: white; }
.action-btn.delete { background-color: #dc3545; color: white; }

.action-btn:hover.view { background-color: #0056b3; }
.action-btn:hover.edit { background-color: #1e7e34; }
.action-btn:hover.delete { background-color: #c82333; }

/* 加载和空状态 */
.loading-row, .empty-row {
  text-align: center;
  padding: 40px 20px;
}

.loading-content, .empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #6c757d;
}

.loading-content i, .empty-content i {
  font-size: 24px;
}

/* 摘要内容 */
.summary-content {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 分页样式 */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

.pagination-info {
  color: #6c757d;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number {
  padding: 6px 10px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  min-width: 36px;
  text-align: center;
  transition: all 0.2s;
}

.page-number:hover {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.page-number.active {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.page-size-select {
  padding: 6px 8px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  background: white;
}

/* 快速工具栏 */
.quick-toolbar {
  position: fixed;
  bottom: 20px;
  left: 260px; /* 避开左侧菜单 */
  right: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
  max-width: calc(100vw - 300px); /* 确保不超出屏幕 */
}

.selected-info {
  font-weight: 500;
  color: #2c3e50;
}

.toolbar-actions {
  display: flex;
  gap: 12px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .dashboard-cards {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }

  .search-panel {
    width: 250px;
  }

  .detail-panel {
    width: 300px;
  }
}

@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
    height: auto;
  }

  .search-panel,
  .detail-panel {
    width: 100%;
  }

  .dashboard-cards {
    grid-template-columns: 1fr;
  }

  .quick-toolbar {
    position: relative;
    bottom: auto;
    left: auto;
    right: auto;
    margin-top: 20px;
  }
}

/* 自定义滚动条 */
.search-content::-webkit-scrollbar,
.detail-content::-webkit-scrollbar,
.relation-list::-webkit-scrollbar,
.ai-history-list::-webkit-scrollbar {
  width: 6px;
}

.search-content::-webkit-scrollbar-track,
.detail-content::-webkit-scrollbar-track,
.relation-list::-webkit-scrollbar-track,
.ai-history-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.search-content::-webkit-scrollbar-thumb,
.detail-content::-webkit-scrollbar-thumb,
.relation-list::-webkit-scrollbar-thumb,
.ai-history-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.search-content::-webkit-scrollbar-thumb:hover,
.detail-content::-webkit-scrollbar-thumb:hover,
.relation-list::-webkit-scrollbar-thumb:hover,
.ai-history-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
